import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Pencil, Trash2, FolderKanban, ExternalLink, Github } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'

import { useProjects } from '@/hooks/useProjects'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui-v2/Button'
import { PageHeader } from '@/components/ui-v2/PageHeader'
import { Badge } from '@/components/ui-v2/Badge'
import { EmptyState } from '@/components/ui-v2/EmptyState'
import { Skeleton } from '@/components/ui-v2/Skeleton'
import { DataTable, DataTableBulkBar, FilterChipsRow, MobilePagination } from '@/components/ui-v2/DataTable'
import { Input } from '@/components/ui-v2/Input'
import { Search } from 'lucide-react'
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogHeader, DialogFooter } from '@/components/ui-v2/Dialog'

interface Project { id: number; name: string; repositoryUrl?: string }

const ProjectList: React.FC = () => {
  const navigate = useNavigate()
  const { isAdmin } = useAuth() as any
  const {
    projects, pagination, loading, error, filters,
    deleteProject, deleteBulkProjects,
    setPage, setPageSize, setFilter, clearFilters,
  } = useProjects({ size: 25 })

  const [search, setSearch] = React.useState((filters.name as string) || '')
  const [selection, setSelection] = React.useState<Record<string, boolean>>({})
  const [confirmDelete, setConfirmDelete] = React.useState<{ kind: 'one' | 'bulk'; ids: number[] } | null>(null)

  React.useEffect(() => {
    const t = setTimeout(() => setFilter('name', search), 300)
    return () => clearTimeout(t)
  }, [search])

  const selectedIds = React.useMemo(
    () => Object.keys(selection).filter((k) => selection[k]).map((k) => Number(k)),
    [selection]
  )

  const columns = React.useMemo<ColumnDef<Project, any>[]>(() => [
    {
      accessorKey: 'id',
      header: () => <span>ID</span>,
      size: 70,
      cell: ({ row }) => <span className="font-mono text-xs text-text-tertiary">#{row.original.id}</span>,
    },
    {
      accessorKey: 'name',
      header: () => <span>Projeto</span>,
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="text-text-primary font-medium">{row.original.name}</span>
          {row.original.repositoryUrl && (
            <a
              href={row.original.repositoryUrl}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-text-secondary hover:text-accent transition-colors inline-flex items-center gap-1 truncate max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <Github className="size-3" />{row.original.repositoryUrl.replace(/^https?:\/\//, '')}
              <ExternalLink className="size-3 opacity-60" />
            </a>
          )}
        </div>
      ),
    },
    {
      id: '__actions',
      header: 'Ações',
      size: 110,
      meta: { align: 'center' },
      cell: ({ row }) => (
        <div className="flex items-center justify-center gap-0.5" onClick={(e) => e.stopPropagation()}>
          <Button size="icon-sm" variant="ghost" onClick={() => navigate(`/projects/${row.original.id}/edit`)} aria-label="Editar" title="Editar">
            <Pencil />
          </Button>
          <Button
            size="icon-sm"
            variant="ghost"
            onClick={() => setConfirmDelete({ kind: 'one', ids: [row.original.id] })}
            aria-label="Excluir"
            title="Excluir"
            className="text-text-secondary hover:text-[var(--danger-strong)]"
          >
            <Trash2 />
          </Button>
        </div>
      ),
    },
  ], [navigate])

  const chips: any[] = []
  if (filters.name) chips.push({ key: 'name', label: 'Nome', value: String(filters.name), onRemove: () => { setSearch(''); setFilter('name', '') } })
  if (filters.repositoryUrl) chips.push({ key: 'repo', label: 'Repo', value: String(filters.repositoryUrl), onRemove: () => setFilter('repositoryUrl', '') })

  return (
    <div>
      <PageHeader
        title="Projetos"
        subtitle={pagination ? `${pagination.totalElements} projeto${pagination.totalElements === 1 ? '' : 's'}` : undefined}
        filters={
          <div className="w-full sm:w-[260px]">
            <Input
              leadingIcon={<Search />}
              placeholder="Buscar por nome..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        }
        actions={isAdmin?.() && (
          <Button leadingIcon={<Plus />} onClick={() => navigate('/projects/create')}>Novo projeto</Button>
        )}
      />

      <FilterChipsRow chips={chips} onClearAll={() => { setSearch(''); clearFilters() }} />

      <DataTableBulkBar
        selectedCount={selectedIds.length}
        onClear={() => setSelection({})}
        actions={isAdmin?.() && (
          <Button size="sm" variant="danger" leadingIcon={<Trash2 />} onClick={() => setConfirmDelete({ kind: 'bulk', ids: selectedIds })}>
            Excluir selecionados
          </Button>
        )}
      />

      <div className="hidden lg:block">
        <DataTable<Project>
          data={projects as any[]}
          columns={columns}
          rowKey={(r) => r.id}
          loading={loading}
          error={error}
          selectable
          selection={selection}
          onSelectionChange={setSelection}
          onRowClick={(r) => navigate(`/projects/${r.id}/edit`)}
          pagination={pagination ? {
            page: pagination.currentPage,
            pageSize: pagination.pageSize,
            total: pagination.totalElements,
            onPageChange: setPage,
            onPageSizeChange: setPageSize,
          } : undefined}
          empty={
            <EmptyState
              icon={<FolderKanban />}
              title="Nenhum projeto"
              description={chips.length > 0 ? 'Ajuste os filtros.' : 'Crie o primeiro projeto.'}
              actions={isAdmin?.() && <Button leadingIcon={<Plus />} onClick={() => navigate('/projects/create')}>Novo projeto</Button>}
            />
          }
        />
      </div>

      <div className="lg:hidden space-y-2">
        {loading && Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}
        {!loading && projects.length === 0 && (
          <EmptyState
            icon={<FolderKanban />}
            title="Nenhum projeto"
            description={chips.length > 0 ? 'Ajuste os filtros.' : 'Crie o primeiro.'}
            actions={isAdmin?.() && <Button leadingIcon={<Plus />} onClick={() => navigate('/projects/create')}>Novo</Button>}
          />
        )}
        {!loading && projects.map((p: any) => (
          <div key={p.id} className="flex items-center gap-2 rounded-lg border border-border-subtle bg-surface-1 py-3 pl-4 pr-2">
            <button onClick={() => navigate(`/projects/${p.id}/edit`)} className="flex-1 min-w-0 text-left">
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-medium text-text-primary truncate">{p.name}</span>
                <Badge size="sm" className="shrink-0">#{p.id}</Badge>
              </div>
              {p.repositoryUrl && (
                <div className="flex items-center gap-1.5 text-xs text-text-secondary mt-1 min-w-0">
                  <Github className="size-3 shrink-0" /><span className="truncate">{p.repositoryUrl.replace(/^https?:\/\//, '')}</span>
                </div>
              )}
            </button>
            <div className="flex items-center gap-0.5 shrink-0">
              <Button size="icon-sm" variant="ghost" onClick={() => navigate(`/projects/${p.id}/edit`)} aria-label="Editar" title="Editar"><Pencil /></Button>
              <Button size="icon-sm" variant="ghost" onClick={() => setConfirmDelete({ kind: 'one', ids: [p.id] })} aria-label="Excluir" title="Excluir" className="text-text-secondary hover:text-[var(--danger-strong)]"><Trash2 /></Button>
            </div>
          </div>
        ))}
      </div>

      {pagination && (
        <MobilePagination
          className="lg:hidden mt-3"
          page={pagination.currentPage}
          pageSize={pagination.pageSize}
          total={pagination.totalElements}
          onPageChange={setPage}
        />
      )}

      <Dialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir {confirmDelete?.kind === 'bulk' ? `${confirmDelete.ids.length} projetos` : 'projeto'}?</DialogTitle>
            <DialogDescription>Esta ação não pode ser desfeita.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setConfirmDelete(null)}>Cancelar</Button>
            <Button
              variant="danger"
              onClick={async () => {
                if (!confirmDelete) return
                try {
                  if (confirmDelete.kind === 'bulk') await deleteBulkProjects(confirmDelete.ids)
                  else await deleteProject(confirmDelete.ids[0])
                  setSelection({})
                } finally {
                  setConfirmDelete(null)
                }
              }}
            >
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default ProjectList
